import type { ReactNode, RefObject } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

export function Dialog({
  open, onOpenChange, children, className, title, description, initialFocus,
}: {
  open: boolean; onOpenChange: (o: boolean) => void; children: ReactNode; className?: string; title: string; description?: string;
  /** What gets focus on open. Unset: the first focusable element. */
  initialFocus?: RefObject<HTMLElement | null>;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          onOpenAutoFocus={initialFocus ? (e) => {
            e.preventDefault();
            initialFocus.current?.focus();
          } : undefined}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-surface shadow-pop outline-none",
            className,
          )}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{description ?? title}</DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function Sheet({
  open, onOpenChange, title, children, width = "w-[440px]",
}: { open: boolean; onOpenChange: (o: boolean) => void; title: ReactNode; children: ReactNode; width?: string }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/25" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-y-2 right-2 z-50 flex max-w-[calc(100vw-16px)] flex-col rounded-2xl border border-border bg-surface shadow-pop outline-none",
            width,
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
            <DialogPrimitive.Title className="text-sm font-semibold">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">Details</DialogPrimitive.Description>
            <DialogPrimitive.Close className="rounded-md p-1 text-subtle hover:bg-surface-2 hover:text-foreground">
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const Menu = DropdownPrimitive.Root;
export const MenuTrigger = DropdownPrimitive.Trigger;
export function MenuContent({ children, align = "end", onCloseAutoFocus }: {
  children: ReactNode;
  align?: "start" | "end";
  /** Call preventDefault to keep focus where an item put it (an input it opened), not back on the trigger. */
  onCloseAutoFocus?: (e: Event) => void;
}) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        align={align}
        onCloseAutoFocus={onCloseAutoFocus}
        sideOffset={6}
        className="z-50 min-w-44 rounded-xl border border-border bg-surface p-1 shadow-pop"
      >
        {children}
      </DropdownPrimitive.Content>
    </DropdownPrimitive.Portal>
  );
}
export function MenuItem({ children, onSelect, danger }: { children: ReactNode; onSelect?: () => void; danger?: boolean }) {
  return (
    <DropdownPrimitive.Item
      onSelect={onSelect}
      className={cn(
        "flex cursor-default items-center gap-2 rounded-lg px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-2",
        danger ? "text-bad" : "text-foreground",
      )}
    >
      {children}
    </DropdownPrimitive.Item>
  );
}
export const MenuSeparator = () => <DropdownPrimitive.Separator className="my-1 h-px bg-border" />;
export const MenuLabel = ({ children }: { children: ReactNode }) => (
  <DropdownPrimitive.Label className="px-2 py-1 text-[11px] font-medium text-subtle">{children}</DropdownPrimitive.Label>
);

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export function PopoverContent({ children, className, align = "start", side }: {
  children: ReactNode;
  className?: string;
  align?: "start" | "end" | "center";
  side?: "top" | "right" | "bottom" | "left";
}) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        side={side}
        sideOffset={6}
        className={cn("z-50 rounded-xl border border-border bg-surface p-1 shadow-pop outline-none", className)}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}
